import {
  Bookmark, Boxes, ClipboardCheck, FileSearch, FileStack, FolderKanban, History, LayoutDashboard,
  Network, Shield, Store,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams,
} from "react-router-dom";
import { Guide } from "./components/Guide";
import { Mark } from "./components/Mark";
import { ErrorState, type ListStatus } from "./components/States";
import { TopBar } from "./components/TopBar";
import { NotAtThisAddress, ValidParams } from "./components/ValidParams";
import { Welcome } from "./components/Welcome";
import { registerPathFromLegacy } from "./components/register/exports";
import { api, uc5, type Job, type UC5Job } from "./lib/api";
import { ApiError } from "./lib/http";
import { atLeast, useMe, type Me, type Role } from "./lib/me";
import { isDocumentId, isName, isTraceId, isUuid } from "./lib/params";
import { markWelcomeSeen, welcomeSeen } from "./lib/welcome";
import { Admin } from "./pages/Admin";
import { AssetPage } from "./pages/AssetPage";
import { CaseDetail } from "./pages/CaseDetail";
import { Cases } from "./pages/Cases";
import { Dashboard } from "./pages/Dashboard";
import { Documents } from "./pages/Documents";
import { DocumentViewScreen } from "./pages/DocumentView";
import { OpenDocument } from "./pages/OpenDocument";
import { Extraction } from "./pages/Extraction";
import { MaintenanceHistory } from "./pages/MaintenanceHistory";
import { ModuleRun } from "./pages/ModuleRun";
import { Modules } from "./pages/Modules";
import { Pid } from "./pages/Pid";
import { Review } from "./pages/Review";
import { ReviewItem } from "./pages/ReviewItem";
import { Saved } from "./pages/Saved";
import { TracePage } from "./pages/Trace";
import { Vendor } from "./pages/Vendor";

export interface NavEntry {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Shown only to this role and above. */
  role?: Role;
  /** Below the spacer, at the foot of the sidebar. */
  foot?: boolean;
}

const NAV: NavEntry[] = [
  { to: "/", label: "Dashboard", icon: <LayoutDashboard /> },
  { to: "/documents", label: "Documents", icon: <FileStack /> },
  { to: "/extraction", label: "Document Spec Extraction", icon: <FileSearch /> },
  { to: "/vendor", label: "Vendor Intelligence", icon: <Store /> },
  { to: "/cases", label: "Cases", icon: <FolderKanban /> },
  { to: "/modules", label: "Modules", icon: <Boxes /> },
  { to: "/pid", label: "P&ID", icon: <Network /> },
  { to: "/history", label: "Maintenance History", icon: <History /> },
  { to: "/review", label: "Review", icon: <ClipboardCheck /> },
  { to: "/saved", label: "Saved", icon: <Bookmark />, foot: true },
  { to: "/admin", label: "Admin", icon: <Shield />, role: "admin", foot: true },
];

/** The sidebar entries this person sees. Admin only for admins; the screen also refuses. */
export function navItems(me: Me | null | undefined): NavEntry[] {
  return NAV.filter((n) => !n.role || atLeast(me, n.role));
}

/** `/extraction` and `/extraction/:jobId`. The URL names the open job. */
function ExtractionRoute({ jobs, status, refresh, query }: { jobs: Job[]; status: ListStatus; refresh: () => void; query: string }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [job, setJobState] = useState<Job | null>(null);
  const [openNew, setOpenNew] = useState(search.get("new") === "1");
  const [missing, setMissing] = useState<unknown>(null);
  const shown = job?.id ?? null;

  useEffect(() => {
    setMissing(null);
    if (!jobId) { setJobState(null); return; }
    if (shown === jobId) return;
    const listed = jobs.find((j) => j.id === jobId);
    if (listed) { setJobState(listed); return; }
    let live = true;
    api.getJob(jobId).then((j) => { if (live) setJobState(j); }).catch((e) => { if (live) setMissing(e); });
    return () => { live = false; };
  }, [jobId, jobs, shown]);

  const setJob = useCallback((j: Job | null) => {
    setJobState(j);
    const target = j ? `/extraction/${j.id}` : "/extraction";
    if ((j?.id ?? undefined) !== jobId) navigate(target);
  }, [jobId, navigate]);

  if (missing instanceof ApiError && [404, 422].includes(missing.status)) {
    return <NotAtThisAddress title="No extraction at this address" back={EXTRACTIONS} />;
  }
  if (missing) {
    return (
      <div className="page">
        <ErrorState error={missing} />
        <Link to="/extraction">Back to all extractions</Link>
      </div>
    );
  }
  return (
    <Extraction jobs={jobs} status={status} job={job} setJob={setJob} refresh={refresh}
                openNew={openNew} setOpenNew={setOpenNew} query={query} />
  );
}

function VendorRoute({ jobs, refresh }: { jobs: Job[]; refresh: () => void }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const onSelect = useCallback((id: string) => navigate(`/vendor/${id}`), [navigate]);
  return <Vendor uc1Jobs={jobs} refresh={refresh} jobId={jobId} onSelect={onSelect} />;
}

const EXTRACTIONS = { to: "/extraction", label: "All extractions" };
const id = { jobId: isUuid };
const registerChecks = { package: isName, tag: isName };
const packageChecks = { package: isName };
const documentChecks = { package: isName, documentId: isDocumentId };

/**
 * An old `/assets/{package}/{tag}` link. nginx hands the app only paths that are not bundle
 * files, and this sends them to the entry's screen under `/register`.
 */
function LegacyRegisterRedirect() {
  const { pathname, search } = useLocation();
  const target = registerPathFromLegacy(pathname);
  return target ? <Navigate to={target + search} replace /> : <NotFound />;
}

function NotFound() {
  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>No screen at this address</h2>
      <p className="muted">The link may be old. <Link to="/">Go to the dashboard</Link>.</p>
    </div>
  );
}

/** Every screen, by URL. Each lane edits its own page file, not this table. */
export function AppRoutes({ jobs, uc5Jobs, query, refresh, jobsStatus = "ready", uc5Status = "ready" }: {
  jobs: Job[]; uc5Jobs: UC5Job[]; query: string; refresh: () => void;
  /** Whether each job list has answered, so a screen says "loading", not "none", before it has. */
  jobsStatus?: ListStatus; uc5Status?: ListStatus;
}) {
  const navigate = useNavigate();
  const open = (j: Job) => navigate(`/extraction/${j.id}`);
  // Each id or name in an address is checked before the screen makes a call with it (SEC-20).
  const extraction = (
    <ValidParams checks={id} title="No extraction at this address" back={EXTRACTIONS}>
      <ExtractionRoute jobs={jobs} status={jobsStatus} refresh={refresh} query={query} />
    </ValidParams>
  );
  const vendor = (
    <ValidParams checks={id} title="No check at this address" back={{ to: "/vendor", label: "All checks" }}>
      <VendorRoute jobs={jobs} refresh={refresh} />
    </ValidParams>
  );
  const documents = (
    <ValidParams checks={packageChecks} title="No package at this address" back={{ to: "/documents", label: "All packages" }}>
      <Documents query={query} onJobStarted={(j) => { refresh(); open(j); }} />
    </ValidParams>
  );
  return (
    <Routes>
      <Route path="/" element={
        <Dashboard jobs={jobs} status={jobsStatus} query={query} onOpen={open} onStart={() => navigate("/extraction?new=1")} />
      } />
      <Route path="/documents" element={documents} />
      <Route path="/documents/open" element={<OpenDocument />} />
      <Route path="/documents/:package" element={documents} />
      <Route path="/documents/:package/doc/:documentId" element={
        <ValidParams checks={documentChecks} title="No document at this address" back={{ to: "/documents", label: "All packages" }}><DocumentViewScreen /></ValidParams>
      } />
      <Route path="/extraction" element={extraction} />
      <Route path="/extraction/:jobId" element={extraction} />
      <Route path="/vendor" element={vendor} />
      <Route path="/vendor/:jobId" element={vendor} />
      <Route path="/history" element={<MaintenanceHistory />} />
      <Route path="/review" element={<Review />} />
      <Route path="/review/:id" element={
        <ValidParams checks={{ id: isUuid }} title="No review item at this address" back={{ to: "/review", label: "Review queue" }}><ReviewItem /></ValidParams>
      } />
      <Route path="/saved" element={<Saved jobs={jobs} uc5Jobs={uc5Jobs} status={jobsStatus} uc5Status={uc5Status} onOpen={open} />} />
      <Route path="/register/:package/:tag" element={
        <ValidParams checks={registerChecks} title="No register entry at this address" back={{ to: "/saved", label: "Saved" }}><AssetPage /></ValidParams>
      } />
      <Route path="/assets/*" element={<LegacyRegisterRedirect />} />
      <Route path="/cases" element={<Cases />} />
      <Route path="/cases/:id" element={
        <ValidParams checks={{ id: isUuid }} title="No case at this address" back={{ to: "/cases", label: "All cases" }}><CaseDetail /></ValidParams>
      } />
      <Route path="/modules" element={<Modules />} />
      <Route path="/modules/runs/:id" element={
        <ValidParams checks={{ id: isUuid }} title="No run at this address" back={{ to: "/modules", label: "All modules" }}><ModuleRun /></ValidParams>
      } />
      <Route path="/traces/:requestId" element={
        <ValidParams checks={{ requestId: isTraceId }} title="No trace at this address" back={{ to: "/modules", label: "All modules" }}><TracePage /></ValidParams>
      } />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/:tab" element={<Admin />} />
      <Route path="/pid" element={<Pid />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/** The screen each address shows, for the browser tab and a screen reader's page title. */
const TITLES: [RegExp, string][] = [
  [/^\/$/, "Dashboard"],
  [/^\/documents\/open$/, "Document"],
  [/^\/documents\/[^/]+\/doc\/.+/, "Document"],
  [/^\/documents(\/.*)?$/, "Documents"],
  [/^\/extraction(\/.*)?$/, "Extraction"],
  [/^\/vendor(\/.*)?$/, "Vendor intelligence"],
  [/^\/history\/?$/, "Maintenance history"],
  [/^\/review\/?$/, "Review"],
  [/^\/review\/.+/, "Review item"],
  [/^\/saved\/?$/, "Saved"],
  [/^\/register\/.+/, "Asset"],
  [/^\/cases\/?$/, "Cases"],
  [/^\/cases\/.+/, "Case"],
  [/^\/modules\/?$/, "Modules"],
  [/^\/modules\/runs\/.+/, "Module run"],
  [/^\/traces\/.+/, "Trace"],
  [/^\/admin(\/.*)?$/, "Admin"],
  [/^\/pid\/?$/, "P&ID"],
];

export function titleFor(pathname: string): string {
  const hit = TITLES.find(([re]) => re.test(pathname));
  return `${hit ? hit[1] : "Not found"} · Obsolescence`;
}

/** The first thing Tab reaches: past the sidebar and the top bar to the screen itself. */
export function SkipLink() {
  return (
    <a className="skip-link" href="#main"
       onClick={(e) => { e.preventDefault(); document.getElementById("main")?.focus(); }}>
      Skip to main content
    </a>
  );
}

const WELCOME_TO = { documents: "/documents", extraction: "/extraction", vendor: "/vendor" } as const;

export function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { me } = useMe();
  useEffect(() => { document.title = titleFor(pathname); }, [pathname]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [uc5Jobs, setUc5Jobs] = useState<UC5Job[]>([]);
  const [jobsStatus, setJobsStatus] = useState<ListStatus>("loading");
  const [uc5Status, setUc5Status] = useState<ListStatus>("loading");
  const [query, setQuery] = useState("");
  const [guide, setGuide] = useState(false);
  // Shown once per person, remembered in localStorage. Not shown until the sign-in is known, so
  // the check is made against the right person.
  const [welcomeClosed, setWelcomeClosed] = useState(false);
  const welcome = me !== undefined && !welcomeClosed && !welcomeSeen(me?.subject);

  const refresh = useCallback(() => {
    // A failed poll after a good answer keeps the list shown; a failure before any answer says so.
    api.listJobs().then((j) => { setJobs(j); setJobsStatus("ready"); })
      .catch((e: Error) => setJobsStatus((s) => (s === "ready" ? s : e)));
    uc5.list().then((j) => { setUc5Jobs(j); setUc5Status("ready"); })
      .catch((e: Error) => setUc5Status((s) => (s === "ready" ? s : e)));
  }, []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 8000); return () => clearInterval(t); }, [refresh]);

  const dismissWelcome = () => { markWelcomeSeen(me?.subject); setWelcomeClosed(true); };
  const items = navItems(me);
  const link = (n: NavEntry) => (
    <NavLink key={n.to} to={n.to} end={n.to === "/"}
             className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
      {n.icon} {n.label}
    </NavLink>
  );

  return (
    <div className="app">
      <SkipLink />
      <nav className="sidebar" aria-label="Main">
        <div className="brandline">
          <Mark />
          <div><h1>Obsolescence Management Agent</h1><div className="sub">Powered by NeIO</div></div>
        </div>
        {items.filter((n) => !n.foot).map(link)}
        <div className="spacer" />
        {items.filter((n) => n.foot).map(link)}
      </nav>

      <div className="content">
        <TopBar jobs={jobs} onOpen={(j) => navigate(`/extraction/${j.id}`)} onGuide={() => setGuide(true)}
                query={query} setQuery={setQuery} />
        <main id="main" tabIndex={-1} className="main-region">
          <AppRoutes jobs={jobs} uc5Jobs={uc5Jobs} query={query} refresh={refresh} jobsStatus={jobsStatus} uc5Status={uc5Status} />
        </main>
      </div>

      {welcome && (
        <Welcome onClose={dismissWelcome}
                 onGo={(v) => { navigate(WELCOME_TO[v]); dismissWelcome(); }} />
      )}
      {guide && <Guide onClose={() => setGuide(false)} />}
    </div>
  );
}
